import Drawer from 'utils/components/drawer';
import type { ComponentType } from '@grafikr/components';
import Cart from '@grafikr/shopify-typescript/type/Cart';
import formatMoney from 'utils/format-money';
import { addItemById } from 'utils/cart';
import { debounce } from 'lodash-es';

const component: ComponentType = (node, { emitter, app }) => {
  const drawer = document.getElementById('cartDrawer');
  const cartDrawer = Drawer(drawer);
  const openButtons = document.querySelectorAll('[data-drawer="cart"]');
  const closeButton = drawer.querySelector('[data-close]');

  const checkout: HTMLElement = drawer.querySelector('button[name="checkout"]');

  const termsCheckbox: HTMLInputElement = drawer.querySelector('input[type="checkbox"]#terms');
  const termsNotice: HTMLElement = drawer.querySelector('#termsNotice');
  const termsAccept: HTMLElement = termsNotice.querySelector('[data-accept]');
  const termsDeny: HTMLElement = termsNotice.querySelector('[data-deny]');

  const itemsContainer: HTMLElement = drawer.querySelector('[data-items]');
  const totalElement: HTMLElement = drawer.querySelector('[data-total]');
  const shippingNotice: HTMLElement = drawer.querySelector('[data-shipping-notice]');
  const shipping: HTMLElement = drawer.querySelector('[data-shipping]');
  const shippingThreshold: number = +shipping.dataset.threshold;

  termsAccept.addEventListener('click', () => {
    termsNotice.classList.remove('active');
    termsCheckbox.checked = true;
    checkout.click();
  });

  termsDeny.addEventListener('click', () => {
    termsNotice.classList.remove('active');
  });

  checkout.addEventListener('click', (e) => {
    if (!termsCheckbox.checked) {
      e.preventDefault();

      termsNotice.classList.add('active');
    }
  });

  const addonCheckbox: NodeListOf<HTMLInputElement> = drawer.querySelectorAll('.js-addon');
  addonCheckbox.forEach((input) => {
    input.addEventListener('change', () => {
      const productId = +input.dataset.product;
      const addonIsInCart = !!theme.cart.items.find((i) => i.id === productId);

      if (input.checked && !addonIsInCart) {
        addItemById({ id: productId, quantity: 1 }).then((cart) => {
          emitter.emit('theme:cart:updated', { cart });
        });
      } else if (!input.checked && addonIsInCart) {
        const data = {
          updates: {},
        };

        data.updates[productId] = 0;

        fetch(`/cart/update.js`, {
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify(data),
        })
          .then((response) => response.json())
          .then((cart) => {
            emitter.emit('theme:cart:updated', { cart });
          });
      }
    });
  });

  const checkFreeProduct = () => {
    if (
      theme.freeProduct &&
      typeof theme.freeProduct === 'number' &&
      !sessionStorage.getItem('dismissFreeGift')
    ) {
      const { items } = theme.cart;
      const freeProductInCart = items.filter((x) => x.variant_id === theme.freeProduct);
      const excludedProducts = theme.freeProductExclusions || [];
      const includedCartItems = items.filter(
        (x) => !excludedProducts.includes(x.product_id) && x.variant_id !== theme.freeProduct
      );
      const totalFiltered = includedCartItems.reduce(
        (accumulator, object) => accumulator + object.final_line_price,
        0
      );

      if (
        freeProductInCart.length === 0 &&
        includedCartItems.length > 0 &&
        totalFiltered >= theme.freeProductThreshold
      ) {
        addItemById({ id: theme.freeProduct, quantity: 1 }).then((cart) => {
          emitter.emit('theme:cart:updated', { cart });
        });
      }

      if (
        (freeProductInCart.length > 0 && includedCartItems.length === 0) ||
        (totalFiltered < theme.freeProductThreshold &&
          includedCartItems.length > 0 &&
          freeProductInCart.length > 0)
      ) {
        const data = {
          updates: {},
        };

        const lineItemKey = items.find(item => item.id === theme.freeProduct).key;

        data.updates[lineItemKey] = 0;

        fetch(`/cart/update.js`, {
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify(data),
        })
          .then((response) => response.json())
          .then((cart) => {
            emitter.emit('theme:cart:updated', { cart });
          });
      }
    }
  };

  const initEventListeners = () => {
    const items: NodeListOf<HTMLLinkElement> = itemsContainer.querySelectorAll('[data-fetch]');
    items.forEach((item) => {
      item.addEventListener('click', async (event) => {
        event.preventDefault();

        const url = new URL(`${window.location.origin + item.dataset.href}`);
        url.pathname = `${url.pathname}.js`;

        if (item.classList.contains('isFreeGift')) {
          sessionStorage.setItem('dismissFreeGift', 'true');
        }

        const response = await fetch(url.href);
        const cart: Cart = await response.json();

        if (cart.item_count === 0) {
          sessionStorage.removeItem('dismissFreeGift');
        }

        emitter.emit('theme:cart:updated', { cart });
      });
    });
  };

  const updatePrices = (cart: Cart) => {
    const isFreeShipping = shippingThreshold
      ? shippingThreshold > 0 && cart.total_price >= shippingThreshold
      : true;

    shipping.textContent = isFreeShipping
      ? theme.strings.freeShipping
      : theme.strings.shippingAtCheckout;

    shippingNotice.hidden = !isFreeShipping;
    totalElement.textContent = formatMoney(cart.total_price, theme.noDecimalsMoneyFormat).toString();
  };

  const updateAddonCheckbox = async () => {
    const addonContainer: HTMLElement = drawer.querySelector('[data-addon-container]');
    const response = await fetch('/cart?view=exclude-re-zip');
    const excludeReZip = await response.json();

    if (excludeReZip.exclude_re_zip === true) {
      addonContainer.classList.add('hidden');
    } else {
      addonContainer.classList.remove('hidden');
    }
  };

  const updateCartCount = (cart: Cart) => {
    const ordinaryItems = cart.items.filter((item) => item.variant_id !== theme.giftWrapProductVariantId);
    const ordinaryItemQty = ordinaryItems.reduce((total, item) => total + item.quantity, 0);

    const cartCountElements = document.querySelectorAll('[data-cart-count]');

    cartCountElements.forEach((element) => {
      element.textContent = ordinaryItemQty.toString();
    });
  };

  const render = debounce((cart: Cart) => {
    checkFreeProduct();
    updatePrices(cart);
    updateCartCount(cart);

    updateAddonCheckbox();

    const url = new URL(`/cart`, window.origin);
    url.searchParams.set('view', 'drawer');

    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        itemsContainer.innerHTML = html;
        initEventListeners();
        setTimeout(app.mount, 0);
      }
    );
  }, 250);

  const updateGiftWrapProducts = async (cart: Cart) => {
    const lineItemsWithGiftWrap = cart.items.filter((item) => item.properties[theme.giftWrapPropertyKey] === theme.giftWrapPropertyValue);
    const targetGiftWrapQty = lineItemsWithGiftWrap.reduce((total, item) => total + item.quantity, 0);
    const giftWrapLineItem = cart.items.find((item) => item.variant_id === theme.giftWrapProductVariantId);

    if (targetGiftWrapQty === giftWrapLineItem?.quantity) {
      return;
    }

    if (giftWrapLineItem !== undefined) {
      const updates = { [giftWrapLineItem.key]: targetGiftWrapQty };

      await fetch('/cart/update.js', {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });
    } else {
      await addItemById({
        id: theme.giftWrapProductVariantId,
        quantity: targetGiftWrapQty
      });
    }
  };

  emitter.on('theme:cart:updated', ({ cart }: { cart: Cart }) => {
    theme.cart = cart;
    render(cart);
    cartDrawer.open();
    updateGiftWrapProducts(cart);
  });

  cartDrawer.on('open', async () => {
    const disableScroll = (
      await import(
        /* webpackChunkName: "scroll-lock" */
        'modules/scroll-lock'
      )
    ).default.disablePageScroll;
    disableScroll(itemsContainer);
  });

  cartDrawer.on('opened', () => {
    emitter.emit('theme:cart:ready');
  });

  cartDrawer.on('close', async () => {
    const enableScroll = (
      await import(
        /* webpackChunkName: "scroll-lock" */
        'modules/scroll-lock'
      )
    ).default;

    enableScroll.clearQueueScrollLocks();
    enableScroll.enablePageScroll(itemsContainer);

    cartDrawer.close();
  });

  openButtons.forEach((button) => {
    button.addEventListener('click', (e) => {
      e.preventDefault();
      cartDrawer.open();
    });
  });

  emitter.on('cart:open', () => {
    cartDrawer.open();
  });

  closeButton.addEventListener('click', () => {
    cartDrawer.close();
  });

  initEventListeners();
  checkFreeProduct();
};

export default component;
